/**
 * Root Motion Analyzer - Professional Foot Contact Synchronization
 * 
 * The key to eliminating foot sliding is to synchronize the character's
 * movement with WHEN each foot is planted on the ground.
 * 
 * Professional approach:
 * 1. Detect exact frames when each foot plants/lifts
 * 2. During a plant phase, the planted foot should NOT move in world space
 * 3. Movement only happens when the PLANTED foot pushes off
 * 4. The stride is distributed across the push-off phases
 */

/**
 * Detect foot contact frames by analyzing foot height (Y position)
 * A foot is "planted" when its Y position is near its minimum height
 * 
 * @param {Array} footPositions - Array of {frame, position: {x, y, z}}
 * @param {number} threshold - Height threshold for ground contact (default 0.02 = 2cm)
 * @returns {Array} Contact data per frame
 */
export function detectFootContacts(footPositions, threshold = 0.02) {
  if (!footPositions || footPositions.length < 2) {
    return footPositions?.map((_, i) => ({ frame: i, isPlanted: false, height: 0 })) || [];
  }
  
  // Find minimum Y (ground level)
  let minY = Infinity;
  for (const fp of footPositions) {
    const y = fp.position?.y ?? fp.y ?? 0;
    if (y < minY) minY = y;
  }
  
  return footPositions.map((fp, i) => {
    const y = fp.position?.y ?? fp.y ?? 0;
    const distFromGround = y - minY;
    return {
      frame: fp.frame ?? i,
      isPlanted: distFromGround < threshold,
      height: y,
      distFromGround
    };
  });
}

/**
 * Find step phases - periods where one foot is planted while the other swings
 * Each step phase represents when the character should be moving
 * 
 * @param {Array} leftContacts - Left foot contacts
 * @param {Array} rightContacts - Right foot contacts
 * @returns {Array} Step phases with frame ranges and which foot is planted
 */
export function findStepPhases(leftContacts, rightContacts) {
  const phases = [];
  let currentPhase = null;
  
  for (let i = 0; i < Math.min(leftContacts.length, rightContacts.length); i++) {
    const leftPlanted = leftContacts[i]?.isPlanted || false;
    const rightPlanted = rightContacts[i]?.isPlanted || false;
    
    // Determine phase type
    let phaseType;
    if (leftPlanted && !rightPlanted) {
      phaseType = 'left_push'; // Left foot pushing, right swinging
    } else if (rightPlanted && !leftPlanted) {
      phaseType = 'right_push'; // Right foot pushing, left swinging
    } else if (leftPlanted && rightPlanted) {
      phaseType = 'double_support'; // Both feet on ground (transition)
    } else {
      phaseType = 'flight'; // Neither foot on ground (running/jumping)
    }
    
    if (!currentPhase || currentPhase.type !== phaseType) {
      if (currentPhase) {
        currentPhase.endFrame = i - 1;
        phases.push(currentPhase);
      }
      currentPhase = {
        type: phaseType,
        startFrame: i,
        endFrame: i
      };
    }
  }
  
  // Close final phase
  if (currentPhase) {
    currentPhase.endFrame = Math.min(leftContacts.length, rightContacts.length) - 1;
    phases.push(currentPhase);
  }
  
  return phases;
}

/**
 * Generate root motion positions using step-synchronized motion
 * 
 * The key principle: Movement happens during push phases, not during swings.
 * This ensures the planted foot stays in place (no sliding).
 * 
 * @param {Array} stepPhases - From findStepPhases
 * @param {number} strideLength - Total distance per full walk cycle
 * @param {number} totalFrames - Total animation frames
 * @returns {Array} Position per frame
 */
export function generateStepSyncedRootMotion(stepPhases, strideLength, totalFrames) {
  // Count push frames (when we should be moving)
  let totalPushFrames = 0;
  for (const phase of stepPhases) {
    if (phase.type === 'left_push' || phase.type === 'right_push') {
      totalPushFrames += (phase.endFrame - phase.startFrame + 1);
    } else if (phase.type === 'double_support') {
      // Slight movement during double support
      totalPushFrames += (phase.endFrame - phase.startFrame + 1) * 0.3;
    }
  }
  
  // Velocity per push frame (to achieve total stride)
  const velocityPerPushFrame = totalPushFrames > 0 ? strideLength / totalPushFrames : strideLength / totalFrames;
  
  // Build position array
  const positions = [];
  let currentZ = 0;
  let currentPhaseIdx = 0;
  
  for (let frame = 0; frame < totalFrames; frame++) {
    // Find current phase
    while (currentPhaseIdx < stepPhases.length - 1 && 
           frame > stepPhases[currentPhaseIdx].endFrame) {
      currentPhaseIdx++;
    }
    
    const phase = stepPhases[currentPhaseIdx];
    let velocity = 0;
    
    if (phase) {
      if (phase.type === 'left_push' || phase.type === 'right_push') {
        // Full velocity during push
        velocity = velocityPerPushFrame;
      } else if (phase.type === 'double_support') {
        // Reduced velocity during transition
        velocity = velocityPerPushFrame * 0.3;
      }
      // No velocity during flight (feet in air)
    }
    
    positions.push({
      frame,
      x: 0,
      y: 0,
      z: currentZ
    });
    
    currentZ += velocity;
  }
  
  return positions;
}

/**
 * Smooth positions using moving average to prevent jerky motion
 */
function smoothPositions(positions, windowSize = 3) {
  const smoothed = [];
  const half = Math.floor(windowSize / 2);
  
  for (let i = 0; i < positions.length; i++) {
    let sumZ = 0;
    let sumY = 0;
    let count = 0;
    
    for (let j = i - half; j <= i + half; j++) {
      if (j >= 0 && j < positions.length) {
        sumZ += positions[j].z;
        sumY += positions[j].y;
        count++;
      }
    }
    
    smoothed.push({
      frame: positions[i].frame,
      x: 0,
      y: sumY / count,
      z: sumZ / count
    });
  }
  
  // Ensure monotonic Z progression (always moving forward)
  for (let i = 1; i < smoothed.length; i++) {
    if (smoothed[i].z < smoothed[i-1].z) {
      smoothed[i].z = smoothed[i-1].z;
    }
  }
  
  return smoothed;
}

/**
 * Extract hip Y positions for vertical bobbing
 */
export function extractHipBobbing(hipPositions) {
  if (!hipPositions || hipPositions.length < 2) {
    return [];
  }
  
  const baseY = hipPositions[0]?.position?.y ?? hipPositions[0]?.y ?? 0;
  return hipPositions.map((pos, i) => ({
    frame: i,
    y: (pos?.position?.y ?? pos?.y ?? 0) - baseY
  }));
}

/**
 * Full analysis and baking pipeline
 * 
 * @param {Object} params
 * @param {Array} params.hipPositions - Hip bone world positions per frame
 * @param {Array} params.leftFootPositions - Left foot world positions per frame  
 * @param {Array} params.rightFootPositions - Right foot world positions per frame
 * @param {number} params.totalFrames - Total animation frames
 * @param {number} params.strideLengthOverride - Optional manual stride length
 * @returns {Object} Complete root motion data
 */
export function analyzeAndBakeRootMotion({
  hipPositions,
  leftFootPositions,
  rightFootPositions,
  totalFrames,
  strideLengthOverride = null
}) {
  console.log('=== Root Motion Analysis ===');
  console.log('Input frames:', { hip: hipPositions?.length, left: leftFootPositions?.length, right: rightFootPositions?.length });
  
  // 1. Detect foot contacts
  const leftContacts = detectFootContacts(leftFootPositions);
  const rightContacts = detectFootContacts(rightFootPositions);
  
  // Debug: count planted frames
  const leftPlantedCount = leftContacts.filter(c => c.isPlanted).length;
  const rightPlantedCount = rightContacts.filter(c => c.isPlanted).length;
  console.log('Foot contacts:', { leftPlanted: leftPlantedCount, rightPlanted: rightPlantedCount });
  
  // 2. Find step phases
  const stepPhases = findStepPhases(leftContacts, rightContacts);
  console.log('Step phases:', stepPhases.map(p => `${p.type}:${p.startFrame}-${p.endFrame}`));
  
  // 3. Determine stride length (default 1.3m for typical walk)
  const strideLength = strideLengthOverride !== null 
    ? Math.max(strideLengthOverride, 0.5)
    : 1.3;
  
  // 4. Generate step-synced root motion
  let bakedPositions = generateStepSyncedRootMotion(stepPhases, strideLength, totalFrames);
  
  // 5. Apply smoothing to prevent jerky motion
  bakedPositions = smoothPositions(bakedPositions, 5);
  
  // 6. Add hip bobbing (Y axis)
  const hipBobbing = extractHipBobbing(hipPositions);
  for (let i = 0; i < bakedPositions.length && i < hipBobbing.length; i++) {
    bakedPositions[i].y = hipBobbing[i].y;
  }
  
  // 7. Normalize Z so final frame = strideLength
  const finalZ = bakedPositions[bakedPositions.length - 1]?.z || 1;
  if (Math.abs(finalZ - strideLength) > 0.01) {
    const scaleFactor = strideLength / finalZ;
    bakedPositions = bakedPositions.map(p => ({
      ...p,
      z: p.z * scaleFactor
    }));
  }
  
  console.log('Final positions range:', { 
    start: bakedPositions[0]?.z?.toFixed(3), 
    end: bakedPositions[bakedPositions.length - 1]?.z?.toFixed(3) 
  });
  
  return {
    needsBaking: true,
    reason: 'Step-synchronized root motion applied',
    bakedPositions,
    strideLength,
    velocityCurve: null,
    footContacts: {
      left: leftContacts,
      right: rightContacts,
      stepPhases
    }
  };
}
